// features/checkout/composables/useCheckoutViewModel.ts
import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePlanSelectionViewModel } from '@/features/planSelection/composables/usePlanSelectionViewModel'
import type { CheckoutFormData } from '@@@/dto/CheckoutFormData'
import CheckoutApiService from '@/services/CheckoutApiService'
import { buildPlanData } from './checkoutDto'
import { validateCheckoutFormClientInformation } from '@@@/validators/checkoutValidators'

const STORAGE_KEY = 'checkout-form-data'

function loadStoredForm(): Partial<CheckoutFormData> {
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
        return typeof raw === 'object' && raw !== null ? raw : {}
    } catch {
        return {}
    }
}

function loadSmartLeadFormAnswers(): Record<string, any> | undefined {
    try {
        const raw = localStorage.getItem('site-builder-answers')
        return raw ? JSON.parse(raw) : undefined
    } catch {
        return undefined
    }
}

export function useCheckoutViewModel() {
    // ----- i18n -----
    const { t } = useI18n()

    // ----- Step & Plan selection -----
    const currentStep = ref(1)
    const billingInterval = ref<'monthly' | 'yearly'>('monthly')
    const { selectedPlanOptions, signupLink } = usePlanSelectionViewModel(billingInterval)

    watch(selectedPlanOptions, () => { }, { immediate: true })

    const totalPrice = computed(() =>
        selectedPlanOptions.value.reduce((sum, opt) => {
            const price = Number(opt.price)
            return sum + (isNaN(price) ? 0 : price)
        }, 0)
    )

    // ----- Form state -----
    const form = reactive<CheckoutFormData>({
        firstname: '',
        lastname: '',
        password: '',
        address1: '',
        postalcode: '',
        city: '',
        state: '',
        country: '',
        email: '',
        phone: '',
        company: '',
        kvk: '',
        vat: '',
        tos: false,
        smartLeadFormAnswers: loadSmartLeadFormAnswers(),
        ...loadStoredForm(),
    })

    const errors = reactive<Record<
        keyof Pick<
            CheckoutFormData,
            'firstname' | 'lastname' | 'password' | 'address1' | 'postalcode' | 'city' | 'country' | 'email' | 'tos'
        >,
        string
    >>({
        firstname: '',
        lastname: '',
        password: '',
        address1: '',
        postalcode: '',
        city: '',
        country: '',
        email: '',
        tos: '',
    })

    watch(
        form,
        () => {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(form))
        },
        { deep: true }
    )

    // ----- Validation -----
    function validateStepClientInformation(): boolean {
        const newErrors = validateCheckoutFormClientInformation(form, t)
        Object.assign(errors, newErrors)
        return Object.values(errors).every(err => err === '')
    }

    function validateInput(field: keyof typeof errors) {
        switch (field) {
            case 'firstname':
                errors.firstname = form.firstname.trim()
                    ? ''
                    : t('checkout.validation.firstnameRequired')
                break

            case 'lastname':
                errors.lastname = form.lastname.trim()
                    ? ''
                    : t('checkout.validation.lastnameRequired')
                break

            case 'password':
                errors.password = form.password.trim()
                    ? ''
                    : t('checkout.validation.passwordRequired')
                break

            case 'address1':
                errors.address1 = form.address1.trim()
                    ? ''
                    : t('checkout.validation.addressRequired')
                break

            case 'postalcode':
                errors.postalcode = form.postalcode.trim()
                    ? ''
                    : t('checkout.validation.postalCodeRequired')
                break

            case 'city':
                errors.city = form.city.trim()
                    ? ''
                    : t('checkout.validation.cityRequired')
                break

            case 'country':
                errors.country = form.country.trim()
                    ? ''
                    : t('checkout.validation.countryRequired')
                break

            case 'email': {
                const emailRx = /^\S+@\S+\.\S+$/
                if (!form.email.trim()) {
                    errors.email = t('checkout.validation.emailRequired')
                } else if (!emailRx.test(form.email)) {
                    errors.email = t('checkout.validation.emailInvalid')
                } else {
                    errors.email = ''
                }
                break
            }

            case 'tos':
                errors.tos = form.tos
                    ? ''
                    : t('checkout.validation.tosAgreementRequired')
                break
        }
    }

    function inputClass(err?: string) {
        const base = 'mt-1 block w-full border rounded p-2 focus:ring focus:ring-offset-1'
        return err ? base + ' border-red-500' : base
    }

    // ----- Submission -----
    async function submitPayment(
        paymentMethod: string,
        interval: 'monthly' | 'yearly'
    ): Promise<string> {
        const payload = {
            checkoutFormData: form,
            planData: buildPlanData(selectedPlanOptions.value, interval, paymentMethod),
        }


        const res = await CheckoutApiService.checkout(payload)
        return res.paymentLink
    }

    // ----- Step navigation -----
    function goToStep(step: number) {
        currentStep.value = step
    }

    return {
        // state
        currentStep,
        billingInterval,
        selectedPlanOptions,
        signupLink,
        totalPrice,
        form,
        errors,

        // actions
        validateStepClientInformation,
        validateInput,
        inputClass,
        goToStep,
        submitPayment,
    }
}
